import { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, ChevronLeft, ChevronRight, Filter, X, Download, Printer, Search,
  FileText, Coins, Receipt, Users, KeyRound, Mail, Settings, History,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIAS } from "@shared/auditoria-frases";
import { useAuditoriaEventos, qsEventos, type EventoComFrase, type ParamsEventos } from "@/hooks/use-auditoria-eventos";
import { apiRequest } from "@/lib/queryClient";
import { EmptyState, LoadingSkeleton } from "@/components/auditoria/estados";

const ICONES: Record<string, typeof FileText> = {
  laudos: FileText, creditos: Coins, faturas: Receipt, cadastros: Users,
  acessos: KeyRound, entregas: Mail, configuracoes: Settings,
};

const ORIGENS: Record<string, string> = {
  cliente: "Site/App", servidor: "Sistema", sistema: "Sistema", webhook: "Integração", cron: "Rotina automática",
};

const TZ = "America/Sao_Paulo";

function categoriaDe(e: EventoComFrase): string {
  const hit = Object.entries(CATEGORIAS).find(
    ([, c]) => c.entidades.includes(e.entidade) || c.tipos?.includes(e.tipo),
  );
  return hit?.[0] ?? "";
}

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
const dia = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { dateStyle: "long", timeZone: TZ });

// Agrupa por dia; dentro do dia, eventos consecutivos com a mesma correlacao_id viram uma "cena".
function agrupar(eventos: EventoComFrase[]) {
  const dias: { titulo: string; cenas: EventoComFrase[][] }[] = [];
  for (const e of eventos) {
    const titulo = dia(e.ocorrido_em);
    let d = dias[dias.length - 1];
    if (!d || d.titulo !== titulo) {
      d = { titulo, cenas: [] };
      dias.push(d);
    }
    const ultima = d.cenas[d.cenas.length - 1];
    if (ultima && e.correlacao_id != null && ultima[0].correlacao_id === e.correlacao_id) ultima.push(e);
    else d.cenas.push([e]);
  }
  return dias;
}

function Linha({
  e, isOwner, onHistorico,
}: { e: EventoComFrase; isOwner: boolean; onHistorico: (e: EventoComFrase) => void }) {
  const [aberto, setAberto] = useState(false);
  const Icone = ICONES[categoriaDe(e)] ?? ShieldCheck;
  // o servidor só envia alteracoes/contexto ao Owner; sem eles não há o que mostrar
  const tecnico = isOwner && (e.alteracoes || e.contexto);

  return (
    <li className="flex gap-3 py-2.5">
      <span className="w-11 shrink-0 pt-0.5 text-xs tabular-nums text-slate-500 dark:text-slate-400">{hora(e.ocorrido_em)}</span>
      <Icone className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-800 dark:text-slate-200">{e.frase}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {e.ator_nome} · {ORIGENS[e.origem] ?? "Sistema"}
          {e.entidade_id && (
            <>
              {" · "}
              <button type="button" onClick={() => onHistorico(e)} className="underline underline-offset-2 hover:text-primary print:hidden">
                Ver histórico
              </button>
            </>
          )}
          {tecnico && (
            <>
              {" · "}
              <button type="button" onClick={() => setAberto((v) => !v)} className="underline underline-offset-2 hover:text-primary print:hidden">
                {aberto ? "Ocultar detalhes técnicos" : "Detalhes técnicos"}
              </button>
            </>
          )}
        </p>
        {tecnico && aberto && (
          <pre className="mt-2 rounded bg-slate-50 dark:bg-slate-800/50 p-2 text-xs font-mono whitespace-pre-wrap break-all text-slate-600 dark:text-slate-400">
            {JSON.stringify({ alteracoes: e.alteracoes, contexto: e.contexto }, null, 2)}
          </pre>
        )}
      </div>
    </li>
  );
}

export function LinhaDoTempo({
  isOwner, empresas,
}: { isOwner: boolean; empresas: { id: string; razaoSocial: string }[] }) {
  const [categoria, setCategoria] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [empresaId, setEmpresaId] = useState("");
  const [busca, setBusca] = useState("");
  const [q, setQ] = useState("");
  const [historico, setHistorico] = useState<{ entidade: string; entidadeId: string; rotulo: string } | null>(null);
  const [page, setPage] = useState(1);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(busca.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [busca]);

  const params: ParamsEventos = {
    categoria: categoria || undefined,
    de: de || undefined,
    ate: ate || undefined,
    empresaId: empresaId || undefined,
    q: q || undefined,
    entidade: historico?.entidade,
    entidadeId: historico?.entidadeId,
    page,
  };
  const { data, isLoading, isError } = useAuditoriaEventos(params);

  const eventos = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (data?.limit ?? 50)));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const dias = useMemo(() => agrupar(eventos), [eventos]);
  const temFiltro = !!(categoria || de || ate || empresaId || busca || historico);

  function limpar() {
    setCategoria(""); setDe(""); setAte(""); setEmpresaId(""); setBusca(""); setQ(""); setHistorico(null); setPage(1);
  }

  async function exportarCsv() {
    setExportando(true);
    try {
      const qs = qsEventos({ ...params, page: undefined });
      const res = await apiRequest("GET", `/api/auditoria/eventos.csv${qs ? `?${qs}` : ""}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "auditoria.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.alert("Não foi possível exportar a auditoria. Tente novamente.");
    } finally {
      setExportando(false);
    }
  }

  const filtrar = (set: (v: string) => void) => (v: string) => {
    set(v === "__all__" ? "" : v);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <Card className="print:hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Filter className="w-4 h-4" /> Filtros</CardTitle>
            {temFiltro && (
              <Button variant="ghost" size="sm" onClick={limpar} className="text-slate-500 hover:text-slate-700 h-7 gap-1">
                <X className="w-3 h-3" /> Limpar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label htmlFor="aud-categoria" className="text-xs">Categoria</Label>
              <Select value={categoria} onValueChange={filtrar(setCategoria)}>
                <SelectTrigger id="aud-categoria" className="h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {Object.entries(CATEGORIAS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isOwner && (
              <div className="space-y-1">
                <Label htmlFor="aud-empresa" className="text-xs">Empresa</Label>
                <Select value={empresaId} onValueChange={filtrar(setEmpresaId)}>
                  <SelectTrigger id="aud-empresa" className="h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.razaoSocial}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="aud-de" className="text-xs">De</Label>
              <Input id="aud-de" type="date" className="h-9" value={de} onChange={(e) => filtrar(setDe)(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="aud-ate" className="text-xs">Até</Label>
              <Input id="aud-ate" type="date" className="h-9" value={ate} onChange={(e) => filtrar(setAte)(e.target.value)} />
            </div>
          </div>
          {de && ate && de > ate && (
            <p className="text-xs text-amber-700 dark:text-amber-500" role="alert">A data inicial é posterior à final.</p>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
            <Input
              className="h-9 pl-9"
              placeholder="Buscar por pessoa, laudo, empresa…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar na auditoria"
            />
          </div>
          {historico && (
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary text-xs px-3 py-1">
              <History className="w-3 h-3" /> Histórico de {historico.rotulo} ·
              <button type="button" onClick={() => { setHistorico(null); setPage(1); }} className="underline underline-offset-2">limpar</button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSkeleton />
          ) : isError ? (
            <EmptyState icon={ShieldCheck} message="Não foi possível carregar a auditoria" />
          ) : eventos.length === 0 ? (
            <EmptyState icon={ShieldCheck} message="Nenhum evento encontrado" />
          ) : (
            <div className="px-4 pb-2">
              {dias.map((d) => (
                <section key={d.titulo}>
                  <h3 className="py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{d.titulo}</h3>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {d.cenas.map((cena) => (
                      <ul
                        key={cena[0].id}
                        className={cena.length > 1 ? "border-l-2 border-primary/40 pl-3 my-1 divide-y divide-slate-50 dark:divide-slate-800/60" : ""}
                      >
                        {cena.map((e) => (
                          <Linha
                            key={e.id}
                            e={e}
                            isOwner={isOwner}
                            onHistorico={(ev) => {
                              setHistorico({ entidade: ev.entidade, entidadeId: ev.entidade_id!, rotulo: ev.entidade_rotulo || "registro" });
                              setPage(1);
                            }}
                          />
                        ))}
                      </ul>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
          {!isLoading && total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 dark:border-slate-800">
              <p className="text-sm text-slate-500 dark:text-slate-400">{total} evento{total !== 1 ? "s" : ""} no total</p>
              <div className="flex flex-wrap items-center gap-2 print:hidden">
                <Button variant="outline" size="sm" onClick={exportarCsv} disabled={exportando} className="h-8 gap-1">
                  <Download className="w-3.5 h-3.5" /> {exportando ? "Exportando…" : "Exportar CSV"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.print()} className="h-8 gap-1">
                  <Printer className="w-3.5 h-3.5" /> Imprimir / PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-8 w-8 p-0" aria-label="Página anterior">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600 dark:text-slate-400 min-w-[80px] text-center">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 w-8 p-0" aria-label="Próxima página">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
